import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LinkTypeStatsClick } from '@app-model/linkTypeStatsClick';
import { PropertySubType, RealEstateFilter, SaleLeaseOptions } from '@app-model/realEstate';
import { DAYS_ON_MARKET_OPTIONS } from '@app-model/realEstate-filter.utils';
import { StatsTrackingService } from '@app-services/stats-tracking.service';
import { LocationSelection } from '../location-picker/location-picker.types';
import { GroupButtonOption } from '../group-button/group-button.component';

@Component({
	selector: 'app-search-filter',
	templateUrl: './search-filter.component.html',
	styleUrls: ['./search-filter.component.scss'],
	encapsulation: ViewEncapsulation.None
})
export class SearchFilterComponent implements OnInit {
	@Input() isModal: boolean;
	@Input() hideFields: boolean;
	@Input() contentId: string;
	@Input() hasRedirect: boolean;
	@Input() isLoading: boolean;
	@Input() entityType: string;
	@Input() entityId: string;

	@Output() toggleModal = new EventEmitter<void>();
	@Output() applyFilters = new EventEmitter<void>();

	filterForm: FormGroup;

	linkType = LinkTypeStatsClick;

	propertyTypes = ['COMMERCIAL', 'RESIDENTIAL', 'LAND'];
	propertySubtypes = Object.values(PropertySubType);

	itemsBathroom = [1, 1.5, 2, 3, 3.5];
	itemsBedroom = [1, 2, 3, 4, 5];

	// Location state managed outside the form
	locationCity?: string;
	locationState?: string;
	latitude?: number;
	longitude?: number;
	locationRadius?: string;
	locationLabel?: string;

	daysOnMarketOptions = DAYS_ON_MARKET_OPTIONS;

	private DEFAULT_VALUES = {
		propertyType: [[]],
		propertySubTypes: [[]],
		priceRangeMin: [''],
		priceRangeMax: [''],
		daysOnMarket: null,
		minYearBuilt: null,
		maxYearBuilt: null,
		saleLeaseOptions: SaleLeaseOptions.ALL
	};

	get locationDisplayText(): string {
		if (this.locationCity) {
			return [this.locationCity, this.locationState].filter(Boolean).join(', ');
		}
		if (this.locationLabel) {
			return this.locationLabel;
		}
		return '';
	}

	/**
	 * Centralized helper to send tracking events for filter changes.
	 * Keeps the payload shape consistent and reduces repeated code.
	 */
	private trackFilterChange(
		linkType: LinkTypeStatsClick,
		previousValue: string | number | string[] | null,
		newValue: string | number | string[] | null
	): void {
		this.statsTracking
			.trackClick(linkType, undefined, undefined, previousValue, newValue)
			.subscribe({ error: () => {} });
	}

	constructor(
		private fb: FormBuilder,
		private router: Router,
		private route: ActivatedRoute,
		private statsTracking: StatsTrackingService
	) {}

	ngOnInit(): void {
		this.filterForm = this.fb.group({
			saleLeaseOptions: [SaleLeaseOptions.ALL],
			propertyType: [[]],
			propertySubTypes: [[]],
			priceRangeMin: [''],
			priceRangeMax: [''],
			minLivingArea: null,
			maxLivingArea: null,
			minBedrooms: null,
			minBathrooms: null,
			daysOnMarket: null,
			minYearBuilt: null,
			maxYearBuilt: null,
			features: [[]]
		});

		this.filterForm.get('priceRangeMin')?.valueChanges.subscribe(value => {
			this.formatPrice('priceRangeMin', value);
		});

		this.filterForm.get('priceRangeMax')?.valueChanges.subscribe(value => {
			this.formatPrice('priceRangeMax', value);
		});

		this.route.queryParams.subscribe(resParams => {
			const params = this.normalizeFilter(resParams);

			// Location fields — extracted individually since latitude/longitude need numeric conversion
			this.locationCity = params['locationCity'] || undefined;
			this.locationState = params['locationState'] || undefined;
			this.locationRadius = params['locationRadius'] || undefined;
			this.locationLabel = params['locationLabel'] || undefined;
			const latRaw = params['latitude'];
			const lngRaw = params['longitude'];
			if (latRaw != null) {
				const latNum = Number(latRaw);
				this.latitude = Number.isNaN(latNum) ? undefined : latNum;
			} else {
				this.latitude = undefined;
			}
			if (lngRaw != null) {
				const lngNum = Number(lngRaw);
				this.longitude = Number.isNaN(lngNum) ? undefined : lngNum;
			} else {
				this.longitude = undefined;
			}

			this.filterForm.patchValue({
				saleLeaseOptions: this.normalizeSaleLeaseOptions(params['saleLeaseOptions']),
				propertyType: Array.isArray(params['propertyType']) ? params['propertyType'] : [],
				propertySubTypes: params['propertySubTypes'] ? params['propertySubTypes'] : [],
				priceRangeMin: params['priceRangeMin'] || this.DEFAULT_VALUES.priceRangeMin,
				priceRangeMax: params['priceRangeMax'] || this.DEFAULT_VALUES.priceRangeMax,
				minLivingArea: params['minLivingArea'] != null ? this.validateNumericParam(params['minLivingArea']) : null,
				maxLivingArea: params['maxLivingArea'] != null ? this.validateNumericParam(params['maxLivingArea']) : null,
				minBedrooms: params['minBedrooms'] != null ? this.validateNumericParam(params['minBedrooms']) : null,
				minBathrooms: params['minBathrooms'] != null ? this.validateNumericParam(params['minBathrooms']) : null,
				daysOnMarket: params['daysOnMarket'] != null ? params['daysOnMarket'] : null,
				minYearBuilt: params['minYearBuilt'] != null ? this.validateNumericParam(params['minYearBuilt']) : null,
				maxYearBuilt: params['maxYearBuilt'] != null ? this.validateNumericParam(params['maxYearBuilt']) : null,
				features: Array.isArray(params['features']) ? params['features'] : []
			});
		});
	}

	onLocationChanged(selection: LocationSelection): void {
		if (selection.type === 'city') {
			this.locationCity = selection.locationCity;
			this.locationState = selection.locationState;
			this.latitude = undefined;
			this.longitude = undefined;
			this.locationRadius = undefined;
			this.locationLabel = undefined;
		} else {
			this.locationCity = undefined;
			this.locationState = undefined;
			this.latitude = selection.latitude;
			this.longitude = selection.longitude;
			this.locationRadius = selection.locationRadius;
			this.locationLabel = selection.displayText;
		}
		this.trackFilterChange(LinkTypeStatsClick.SEARCH_FILTER_LOCATION_CHANGE, null, selection.displayText);
	}

	onLocationCleared(): void {
		this.locationCity = undefined;
		this.locationState = undefined;
		this.latitude = undefined;
		this.longitude = undefined;
		this.locationRadius = undefined;
		this.locationLabel = undefined;
	}

	onCheckboxChange(event: Event, controlName: string, linkType?: LinkTypeStatsClick) {
		const input = event.target as HTMLInputElement;
		const currentValue = this.filterForm.get(controlName)?.value;

		const selected: string[] = currentValue ? (Array.isArray(currentValue) ? currentValue : [currentValue]) : [];

		const updated = input.checked ? [...selected, input.value] : selected.filter(v => v !== input.value);

		this.filterForm.get(controlName)?.setValue(updated);

		if (linkType) {
			this.statsTracking.trackClick(linkType, undefined, undefined, selected, updated).subscribe();
		}
	}

	onClearFilters(): void {
		this.statsTracking.trackClick(LinkTypeStatsClick.SEARCH_FILTER_CLEAR_ALL).subscribe();

		this.filterForm.reset();

		this.filterForm.patchValue({
			saleLeaseOptions: this.DEFAULT_VALUES.saleLeaseOptions,
			propertyType: [],
			propertySubTypes: [],
			priceRangeMin: this.DEFAULT_VALUES.priceRangeMin,
			priceRangeMax: this.DEFAULT_VALUES.priceRangeMax,
			minLivingArea: null,
			maxLivingArea: null,
			minBedrooms: null,
			minBathrooms: null,
			daysOnMarket: null,
			minYearBuilt: null,
			maxYearBuilt: null,
			features: []
		});

		this.onLocationCleared();

		this.applyFilters.emit(this.filterForm.value as any);
	}

	onApplyFilters(): void {
		this.statsTracking.trackFormSubmit('filter', this.filterForm.value).subscribe();

		const filters = this.filterForm.value;

		if (this.hasRedirect) {
			const currentUrl = this.router.url;

			const queryParams = {
				locationCity: this.locationCity,
				locationState: this.locationState,
				latitude: this.latitude != null && isFinite(this.latitude) ? String(this.latitude) : undefined,
				longitude: this.longitude != null && isFinite(this.longitude) ? String(this.longitude) : undefined,
				locationRadius: this.locationRadius,
				locationLabel: this.locationLabel,
				saleLeaseOptions: filters.saleLeaseOptions,
				propertyType: filters.propertyType,
				propertySubTypes: filters.propertySubTypes,
				priceRangeMin: filters.priceRangeMin,
				priceRangeMax: filters.priceRangeMax,
				minLivingArea: filters.minLivingArea,
				maxLivingArea: filters.maxLivingArea,
				minBedrooms: filters.minBedrooms,
				minBathrooms: filters.minBathrooms,
				daysOnMarket: filters.daysOnMarket,
				minYearBuilt: filters.minYearBuilt,
				maxYearBuilt: filters.maxYearBuilt,
				features: filters.features
			};

			let navigatePromise: Promise<boolean>;

			if (currentUrl.includes('/search/')) {
				navigatePromise = this.router.navigate([], {
					relativeTo: this.route,
					queryParams,
					queryParamsHandling: 'merge'
				});
			} else if (currentUrl.includes('/showcase/')) {
				const newUrl = currentUrl.replace(/\/showcase\/.*$/, '');
				navigatePromise = this.router.navigate([`${newUrl}/search/${this.contentId}`], {
					queryParams
				});
			} else {
				const newUrl = `${currentUrl}/search/${this.contentId}`;
				navigatePromise = this.router.navigate([newUrl], {
					queryParams,
					queryParamsHandling: 'merge'
				});
			}

			navigatePromise.then(() => {
				this.applyFilters.emit();
			});
		} else {
			this.applyFilters.emit({
				...filters,
				locationCity: this.locationCity,
				locationState: this.locationState,
				latitude: this.latitude,
				longitude: this.longitude,
				locationRadius: this.locationRadius,
				locationLabel: this.locationLabel
			} as any);
		}
	}

	onToggleModal() {
		this.toggleModal.emit();
	}

	getPropertyTypeLinkType(type: string): LinkTypeStatsClick {
		switch (type) {
			case 'COMMERCIAL':
				return LinkTypeStatsClick.SEARCH_FILTER_TYPE_COMMERCIAL;
			case 'RESIDENTIAL':
				return LinkTypeStatsClick.SEARCH_FILTER_TYPE_RESIDENTIAL;
			case 'LAND':
				return LinkTypeStatsClick.SEARCH_FILTER_TYPE_LAND;
			default:
				return LinkTypeStatsClick.SEARCH_FILTER_TYPE_COMMERCIAL;
		}
	}

	private normalizeFilter(raw: { [key: string]: string | string[] }) {
		const arrayFields = ['propertySubTypes', 'propertyType', 'features'];

		const normalized: RealEstateFilter = {};

		Object.entries(raw).forEach(([key, value]) => {
			const cleanKey = key.endsWith('[]') ? key.slice(0, -2) : key;

			if (Array.isArray(value)) {
				if (value.length === 0 || (value.length === 1 && value[0] === '')) {
					return;
				}

				const normalizedItems = value
					.reduce((items, item) => items.concat(String(item || '').split(',')), [] as string[])
					.map(item => item.trim())
					.filter(Boolean);

				if (!normalizedItems.length) {
					return;
				}

				if (arrayFields.includes(cleanKey)) {
					const existing = normalized[cleanKey] as string[] | undefined;
					normalized[cleanKey] = existing ? [...existing, ...normalizedItems] : normalizedItems;
				} else {
					normalized[cleanKey] = normalizedItems[0];
				}
			} else {
				if (value !== '') {
					if (arrayFields.includes(cleanKey)) {
						normalized[cleanKey] = value
							.split(',')
							.map(item => item.trim())
							.filter(item => !!item);
					} else {
						normalized[cleanKey] = value;
					}
				}
			}
		});

		return normalized;
	}

	private formatPrice(controlName: string, value: string): void {
		if (!value || value[0] === '') {
			return;
		}

		let numericValue: number | undefined;
		if (Array.isArray(value) && typeof value[0] === 'string' && value[0]) {
			numericValue = Number(value[0].replace(/\D/g, ''));
		} else if (typeof value === 'string' && value) {
			numericValue = Number(value.replace(/\D/g, ''));
		}
		if (numericValue === undefined) {
			return;
		}

		if (isNaN(numericValue)) {
			return;
		}

		const formatted = new Intl.NumberFormat('en-US').format(numericValue);
		this.filterForm.get(controlName)?.setValue(formatted, { emitEvent: false });
	}

	public onFilterChanged(field: string, event?: any): void {
		let currentValue: any;
		let previousValue: any;
		let linkType: LinkTypeStatsClick | null = null;

		switch (field) {
			case 'priceRangeMin':
				currentValue = this.filterForm.get('priceRangeMin')?.value || '';
				linkType = LinkTypeStatsClick.SEARCH_FILTER_PRICE_MIN_CHANGE;
				break;
			case 'priceRangeMax':
				currentValue = this.filterForm.get('priceRangeMax')?.value || '';
				linkType = LinkTypeStatsClick.SEARCH_FILTER_PRICE_MAX_CHANGE;
				break;
			case 'minBedrooms':
				currentValue = event !== undefined ? event : this.filterForm.get('minBedrooms')?.value;
				linkType = LinkTypeStatsClick.SEARCH_FILTER_BEDROOMS_CHANGE;
				break;
			case 'minBathrooms':
				currentValue = event !== undefined ? event : this.filterForm.get('minBathrooms')?.value;
				linkType = LinkTypeStatsClick.SEARCH_FILTER_BATHROOMS_CHANGE;
				break;
			default:
				return;
		}

		if (currentValue === previousValue) {
			return;
		}

		this.trackFilterChange(linkType!, previousValue, currentValue);

		switch (field) {
			case 'priceRangeMin':
				break;
			case 'priceRangeMax':
				break;
			case 'minBedrooms':
				break;
			case 'minBathrooms':
				break;
		}
	}

	private validateNumericParam(value: any): number | null {
		const num = Number(value);
		return isNaN(num) ? null : num;
	}

	private normalizeSaleLeaseOptions(value: unknown): SaleLeaseOptions {
		if (typeof value === 'string' && Object.values(SaleLeaseOptions).includes(value as SaleLeaseOptions)) {
			return value as SaleLeaseOptions;
		}

		return SaleLeaseOptions.ALL;
	}

	get saleLeaseOptions(): GroupButtonOption<SaleLeaseOptions>[] {
		return Object.values(SaleLeaseOptions).map(option => ({
			value: option,
			labelKey: `promotedContents.realEstateFilterOptions.saleLeaseOptions.${option}`
		}));
	}
}
